import CalendarDailyDetails from "@/components/calendar/calendar-daily-details";
import { CalendarList } from "@/components/calendar/calendar-list";
import CaseEventsModal from "@/components/cases/case-calendar-events-modal";
import CaseEventsModalEdit from "@/components/cases/case-calendar-events-modal-edit";
import CaseHearingModal from "@/components/cases/case-calendar-hearing-modal";
import CaseEventsDeleteModal from "@/components/cases/case-events-delete-modal";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button.tsx";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller.tsx";
import { Content } from "@/components/layouts/content";
import { entityType } from "@/constants/entity-types.ts";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { calendarsQueryOptions } from "@/features/dashboard/api/queries/calendar-queries.ts";
import { eventsQueryOptions } from "@/features/dashboard/api/queries/event-queries.ts";
import { usecaseEventsDeleteMutation } from "@/features/dashboard/api/queries/event-queries.ts";
import userService from "@/features/dashboard/api/services/user-service";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import { IconClock, IconNote } from "@tabler/icons-react";
import { IconArchive, IconChevronDown, IconDots, IconPencilMinus, IconSearch, IconTerminal2, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { useRef, useState } from "react";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/calendar")({
	component: Page,
	validateSearch: z.object({
		calendars: z.array(z.string()).optional(),
		date: z.string().optional(),
		view: z.boolean().default(false),
	}),
	loaderDeps: ({ search }) => ({
		calendars: search.calendars,
		date: search.date,
		view: search.view,
	}),
	loader: ({ context: { queryClient }, deps, params }) => {
		queryClient.ensureQueryData(
			calendarsQueryOptions({
				...params,
				entity_type: entityType.USER,
			}),
		);

		return queryClient.ensureQueryData(
			eventsQueryOptions({
				...deps,
				date: deps.date,
			}),
		);
	},
});

function Page() {
	const [eventCounts, setEventCounts] = useState({});
	const modal = useModal();
	const calendarRef = useRef<FullCalendar | null>(null);
	const search = Route.useSearch();
	const calendarsQueries = useSuspenseQuery(
		calendarsQueryOptions({
			entity_type: entityType.USER,
		}),
	);
	const { user } = useAuth();
	const calendars = calendarsQueries.data;
	const eventsQuery = useSuspenseQuery(
		eventsQueryOptions({
			calendars: isEmpty(search.calendars) && calendars.data.length !== 0 ? calendars.data.map((cal) => cal.id) : search.calendars,
			branch_id: user?.branches?.[0]?.assignable?.id,
		}),
	);
	const events = eventsQuery.data;

	useEffect(() => {
		if (events.data) {
			const counts = events.data.reduce((acc, event) => {
				const dateKey = new Date(event.start).toISOString().split("T")[0];
				acc[dateKey] = (acc[dateKey] || 0) + 1;
				return acc;
			}, {});
			setEventCounts(counts);
		}
	}, [events.data]);

	const [calendarView, setCalendarView] = useState(!search.view);
	const [date, setDate] = useState("");
	const [error, setError] = useState(null);

	const handleDateClick = async (info: { dateStr: string }) => {
		setCalendarView((prev) => !prev);
		setDate(info.dateStr);
	};

	useEffect(() => {
		if (search.date !== null || search.date !== "") {
			setDate(search.date || "");
		}
	}, [search.date]);

	const eventsDatas = calendarsQueries.data.data;

	const openHearingModal = () => {
		const userEventCalendar = eventsDatas.find((calendar) => calendar.type === "Events");

		const calendarId = userEventCalendar?.id;
		const branchId = user?.branches?.[0]?.assignable?.id;

		const modalId = modal.openModal({
			allowClose: true,
			title: "Add Hearing Schedule",
			size: "xl",
			showHeader: true,
			showFooter: false,
			component: (
				<CaseHearingModal
					calendarId={calendarId}
					branchId={branchId}
					handleSubmit={(values) => {
						console.log(values, "testing");
					}}
				/>
			),
		});
	};

	const openEventsModal = () => {
		const userEventCalendar = eventsDatas.find((calendar) => calendar.type === "Events");

		const calendarId = userEventCalendar?.id;
		const branchId = user?.branches?.[0]?.assignable?.id;

		const modalId = modal.openModal({
			allowClose: true,
			title: "Add Events Schedule",
			size: "xl",
			showHeader: true,
			showFooter: false,
			component: (
				<CaseEventsModal
					calendarId={calendarId}
					branchId={branchId}
					handleSubmit={(values) => {
						console.log(values, "testing");
					}}
				/>
			),
		});
	};
	const caseEventsDeleteMutation = usecaseEventsDeleteMutation();

	const formattedEvents = events.data.flatMap((e) => {
		const start = new Date(e.start_at);
		const end = e.end_at ? new Date(e.end_at) : start;

		const adjustedEnd = new Date(end.getTime() + 24 * 60 * 60 * 1000);

		return {
			...e,
			start: start.toISOString(),
			end: adjustedEnd.toISOString(),
			allDay: true,
		};
	});

	return (
		<Content>
			<div className="flex flex-row w-full h-full">
				{/* <div className="realtive inline-flex flex-col shrink-0 w-[380px]">
					<div className="fixed left-0 bottom-0 top-[50px] w-[380px] border-r border-border/50">
						<ScrollArea>
							<div className="flex flex-col w-full h-[calc(100svh-50px)]">
								<CalendarList />
							</div>
						</ScrollArea>
					</div>
				</div> */}
				<div className="flex flex-col w-full">
					{calendarView ? (
						<div className="custom-full-calendar flex flex-col w-full">
							<FullCalendar
								dayCellContent={(args) => {
									const viewType = args.view.type;
									const currentDate = args.date.toDateString();
									const counts = events.data.filter((e) => e.start && new Date(e.start).toDateString() === currentDate).length;

									if (viewType !== "dayGridMonth") return null;

									return (
										<div className="flex flex-row items-center justify-between w-full h-full">
											<div className="dateNumber inline-flex justify-center items-center w-[24px] h-[24px] rounded">{args.dayNumberText}</div>
											{counts > 0 && (
												<div className="inline-flex flex-row items-center justify-center h-[24px] px-2 rounded-md bg-primary-100 text-primary-600 text-xs uppercase font-semibold whitespace-nowrap">
													{counts} event{counts > 1 ? "s" : ""}
												</div>
											)}
										</div>
									);
								}}
								ref={calendarRef}
								schedulerLicenseKey="XXX"
								initialView="dayGridMonth"
								stickyHeaderDates={false}
								plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
								eventColor="#797A7E"
								eventOrder="end,-start"
								// events={events.data}
								events={formattedEvents}
								dateClick={handleDateClick}
								headerToolbar={{
									start: "prev,next todayHighlight",
									center: "title",
									end: "addDropdown,dayGridMonth,dayGridWeek",
								}}
								buttonText={{
									dayGridMonth: "Month",
									dayGridWeek: "Week",
								}}
								customButtons={{
									todayHighlight: {
										text: "Today",
										click: () => calendarRef.current?.getApi().today(),
									},
									// addHearingBtn: {
									// 	text: "+ Add Hearing",
									// 	click: () => {
									// 		openHearingModal();
									// 	},
									addDropdown: {
										text: "+ Add",
										click: () => {
											const menu = document.getElementById("fc-addDropdown-menu");
											if (menu) {
												menu.style.display = menu.style.display === "block" ? "none" : "block";
											}
										},
									},
								}}
								eventContent={({ event }) => {
									const totalEvents = event.extendedProps.totalEvents || 0;
									// const color = event._def?.ui?.backgroundColor;
									let color = event._def?.ui?.backgroundColor;
									if (event.extendedProps.type === "events") {
										color = "#FF6E00";
									}

									const openEditModal = (eventData: any) => {
										modal.openModal({
											title: "Events Details",
											size: "lg",
											component: <CaseEventsModalEdit eventsData={eventData} handleSubmit={() => {}} />,
										});
									};
									const startAt = event.extendedProps.start_at ? moment(event.extendedProps.start_at) : null;
									const timeDisplay = startAt ? startAt.format("hh:mm A") : "All Day";

									return (
										<div className="flex flex-col w-full px-2 py-1 rounded" style={{ backgroundColor: color, color: "white" }}>
											{event.extendedProps.type !== "holiday" && (
												<div className="flex flex-row items-center justify-between w-full gap-1">
													<div className="inline-flex flex-row items-center w-auto gap-[1px]">
														{/* <span className="text-xs leading-tight">
															{new Date(event.start).toLocaleTimeString([], {
																hour: "2-digit",
																minute: "2-digit",
															})}
														</span> */}
														<span className="text-xs leading-tight">{timeDisplay}</span>
													</div>
													<div className="inline-flex flex-row items-center gap-[1px]">
														<span className="text-xs leading-tight">{event.extendedProps.caseRecord?.case_number}</span>
													</div>
													{event.extendedProps.type === "events" && (
														<div className="inline-flex flex-row items-center gap-[1px]">
															<DropdownMenu modal={false}>
																<DropdownMenuTrigger asChild>
																	<Button className="!rounded-full !w-[16px] !h-[16px]" size="icon" variant="ghost">
																		<IconDots size={16} stroke={1.5} />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent>
																	<DropdownMenuGroup>
																		<DropdownMenuItem onClick={() => openEditModal(event)} className="cursor-pointer">
																			<div className="flex flex-row items-center w-full py-1 px-2 gap-5">
																				<span>Edit</span>
																			</div>
																		</DropdownMenuItem>
																		<DropdownMenuItem
																			onClick={() => {
																				const modalId = modal.openModal({
																					allowClose: true,
																					title: "Delete Events Details	",
																					size: "md",
																					showHeader: true,
																					showFooter: false,
																					component: (
																						<CaseEventsDeleteModal
																							data={event}
																							handleSubmit={async (values) => {
																								const response = await userService.validatePassword(values.password);
																								if (response.message === "Valid") {
																									setError(null);
																									sessionStorage.setItem("password_entered_at", moment());
																									if (event?.extendedProps?.event_id) {
																										await caseEventsDeleteMutation.mutateAsync(event.extendedProps.event_id);
																									}
																									modal.closeModal(modalId);
																								} else {
																									setError(response.message);
																									toast.error("Invalid password. Please try again.");
																								}
																							}}
																						/>
																					),
																				});
																			}}
																		>
																			<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																				<span>Delete</span>
																			</div>
																		</DropdownMenuItem>
																	</DropdownMenuGroup>
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													)}
												</div>
											)}
											<div className="flex flex-col w-full">
												<span className="text-xs font-semibold leading-tight truncate">{event.title}</span>
											</div>
										</div>
									);
								}}
								eventClick={async (info) => {
									const caseRecord = info.event.extendedProps;

									if (caseRecord.type !== "holiday" && caseRecord.type !== "events") {
										const records = Array.isArray(caseRecord) ? caseRecord : [caseRecord];

										modal.openModal({
											title: info.event.title,
											size: "lg",
											component: <EventTable records={records} />,
										});
									}
								}}
							/>
						</div>
					) : (
						<CalendarDailyDetails date={date} calendarView={calendarView} handleDateClick={handleDateClick} />
					)}
				</div>
				<div
					id="fc-addDropdown-menu"
					style={{
						display: "none",
						position: "absolute",
						right: "185px",
						top: "65px",
						background: "#fff",
						borderTop: "1px solid #ddd",
						borderRadius: "8px",
						boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
						zIndex: 1000,
						minWidth: "160px",
						overflow: "hidden",
						fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
						fontSize: "12px",
					}}
				>
					{["Add Hearing", "Add Events"].map((label, index) => (
						<button
							key={index}
							type="button"
							style={{
								padding: "4px 12px",
								cursor: "pointer",
								background: "white",
								border: "none",
								width: "100%",
								textAlign: "left",
								transition: "background 0.2s, color 0.2s",
								fontFamily: "inherit",
							}}
							onClick={() => {
								const menu = document.getElementById("fc-addDropdown-menu");
								if (menu) menu.style.display = "none";
								index === 0 ? openHearingModal() : openEventsModal();
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.background = "#f5f5f5";
								e.currentTarget.style.color = "#333";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.background = "white";
								e.currentTarget.style.color = "inherit";
							}}
						>
							{label}
						</button>
					))}
				</div>
			</div>
		</Content>
	);
}

function EventTable({
	records,
}: {
	records: CaseRecord[];
}) {
	const { closeModal } = useModalControl();
	const { user } = useAuth();
	const isOcc = user?.is_occ;

	const columns: ColumnDef<CaseRecord>[] = [
		{
			id: "name",
			label: "Case Title",
			cell: ({ data }) =>
				isOcc ? (
					<Link
						onClick={closeModal}
						className="text-blue-600"
						to="/case-classification/$caseClassificationId"
						params={{ caseClassificationId: data?.caseRecord?.id }}
					>
						{data?.caseRecord?.case_title}
					</Link>
				) : (
					<Link onClick={closeModal} className="text-blue-600" to="/case-evaluation/$caseEvaluationId" params={{ caseEvaluationId: data?.caseRecord?.id }}>
						{data?.caseRecord?.case_title}
					</Link>
				),
		},
		{
			id: "case_number",
			label: "Case Number",
			cell: ({ data }) => data?.caseRecord?.case_number,
		},
		// {
		// 	id: "branch",
		// 	label: "Branch",
		// 	cell: ({ data }) =>  data?.caseRecord?.branch?.name,
		// },
		{
			id: "name",
			label: "Date Filed",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => formatDate(data?.filed_at),
		},
	];

	return <DataTable columns={columns} data={records} />;
}
